import { addToolBreadcrumb } from '@tunasong/models'
import {
  ALL_SOURCE_KEYS,
  computeModulationSessionStats,
  generateModulationQuizQuestion,
} from '@tunasong/music-lib'
import type {
  ModulationQuizQuestion,
  ModulationQuizResult,
  ModulationQuizVerdict,
  ModulationTechnique,
  SourceKey,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState, type FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { ModulationExplorePanel } from './modulation-explore-panel.js'
import { ModulationLabSummary } from './modulation-lab-summary.js'
import { ModulationLabWelcome, type ModulationQuizConfig } from './modulation-lab-welcome.js'
import {
  parseAuditionLength,
  parseDifficulty,
  parseDestinationKey,
  parseModulationSubMode,
  parseSourceKey,
  parseTechnique,
  type AuditionLength,
  type Difficulty,
  type ModulationSubMode,
} from './modulation-lab-params.js'
import { ModulationQuizFeedback } from './modulation-quiz-feedback.js'
import { ModulationQuizQuestion as QuizQuestionPanel } from './modulation-quiz-question.js'
import { ModulationSourceKeyPicker } from './modulation-source-key-picker.js'
import styles from './modulation-lab.module.css'

const QUESTIONS_PER_SESSION = 10

const RELATED_TOOLS: RelatedTool[] = [
  { targetRoute: '/app/circle', lexiconId: 'crosslink-modulation-to-cof' },
  { targetRoute: '/app/reharm', lexiconId: 'crosslink-modulation-to-reharm' },
  { targetRoute: '/music-theory', lexiconId: 'crosslink-modulation-to-suite' },
]

interface QuizConfig {
  length: AuditionLength
  difficulty: Difficulty
}

type SessionState =
  | { phase: 'welcome'; preSubMode: ModulationSubMode | null }
  | {
      phase: 'exploring'
      source: SourceKey
      technique: ModulationTechnique
      destination: string
    }
  | {
      phase: 'asking-quiz'
      index: number
      question: ModulationQuizQuestion
      results: ModulationQuizResult[]
      config: QuizConfig
    }
  | {
      // Hard-mode second answer phase: technique already picked, now pick source.
      phase: 'asking-source'
      index: number
      question: ModulationQuizQuestion
      results: ModulationQuizResult[]
      config: QuizConfig
      pickedTechnique: ModulationTechnique
    }
  | {
      phase: 'feedback-quiz'
      index: number
      question: ModulationQuizQuestion
      verdict: ModulationQuizVerdict
      results: ModulationQuizResult[]
      config: QuizConfig
    }
  | { phase: 'done-quiz'; results: ModulationQuizResult[] }

const buildQuizQuestion = (config: QuizConfig): ModulationQuizQuestion =>
  generateModulationQuizQuestion(Math.random, {
    sourcePool: ALL_SOURCE_KEYS,
    length: config.length,
  })

/**
 * Modulation Lab v1 host. State machine: welcome → exploring | asking-quiz →
 * feedback-quiz → done-quiz. URL params `?mode=`, `?from=`, `?to=`,
 * `?technique=` pre-select at initial mount.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §7
 */
export const ModulationLab: FC = () => {
  const [searchParams] = useSearchParams()

  const [state, setState] = useState<SessionState>(() => {
    const preSubMode = parseModulationSubMode(searchParams.get('mode'))
    const preSource = parseSourceKey(searchParams.get('from'))
    const preTechnique = parseTechnique(searchParams.get('technique'))
    const config: QuizConfig = {
      length: parseAuditionLength(searchParams.get('length')),
      difficulty: parseDifficulty(searchParams.get('difficulty')),
    }
    if (preSubMode === 'quiz') {
      return {
        phase: 'asking-quiz',
        index: 0,
        question: buildQuizQuestion(config),
        results: [],
        config,
      }
    }
    if (preSubMode === 'explore') {
      const source = preSource ?? 'C'
      const technique = preTechnique ?? 'pivot'
      const preDestination = parseDestinationKey(searchParams.get('to'), source, technique)
      const destination = preDestination ?? (technique === 'parallel' ? `${source}m` : 'G')
      return { phase: 'exploring', source, technique, destination }
    }
    return { phase: 'welcome', preSubMode }
  })

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'modulation-lab' })
  }, [])

  const startExplore = (): void => {
    addToolBreadcrumb('opened', { tool: 'modulation-lab', mode: 'explore' })
    setState({ phase: 'exploring', source: 'C', technique: 'pivot', destination: 'G' })
  }

  const startQuiz = (config: ModulationQuizConfig): void => {
    addToolBreadcrumb('opened', {
      tool: 'modulation-lab',
      mode: 'quiz',
      length: config.length,
      difficulty: config.difficulty,
    })
    setState({
      phase: 'asking-quiz',
      index: 0,
      question: buildQuizQuestion(config),
      results: [],
      config,
    })
  }

  // Resolve a technique pick into a verdict + advance. In easy mode the verdict
  // is technique-correctness alone; in hard mode it requires the source key too.
  const settle = (
    base: Extract<SessionState, { phase: 'asking-quiz' | 'asking-source' }>,
    pickedTechnique: ModulationTechnique,
    pickedSource: string | null
  ): void => {
    const techniqueCorrect = pickedTechnique === base.question.acceptedAnswer
    const sourceCorrect =
      base.config.difficulty === 'easy' || pickedSource === base.question.plan.sourceKey
    const correct = techniqueCorrect && sourceCorrect
    const verdict: ModulationQuizVerdict = {
      correct,
      picked: pickedTechnique,
      question: base.question,
    }
    addToolBreadcrumb('answered', {
      tool: 'modulation-lab',
      subMode: 'quiz',
      difficulty: base.config.difficulty,
      correct,
      technique: base.question.acceptedAnswer,
    })
    setState({
      phase: 'feedback-quiz',
      index: base.index,
      question: base.question,
      verdict,
      results: base.results,
      config: base.config,
    })
  }

  const handleAnswer = (picked: ModulationTechnique): void => {
    if (state.phase !== 'asking-quiz') {return}
    if (picked === 'deceptive') {
      addToolBreadcrumb('selected', { tool: 'modulation-lab', subMode: 'quiz', technique: 'deceptive' })
    }
    if (state.config.difficulty === 'hard') {
      setState({
        phase: 'asking-source',
        index: state.index,
        question: state.question,
        results: state.results,
        config: state.config,
        pickedTechnique: picked,
      })
      return
    }
    settle(state, picked, null)
  }

  const handleSourcePick = (source: string): void => {
    if (state.phase !== 'asking-source') {return}
    settle(state, state.pickedTechnique, source)
  }

  const handleNext = (): void => {
    if (state.phase !== 'feedback-quiz') {return}
    const updated: ModulationQuizResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quiz', results: updated })
      return
    }
    setState({
      phase: 'asking-quiz',
      index: state.index + 1,
      question: buildQuizQuestion(state.config),
      results: updated,
      config: state.config,
    })
  }

  const exitToWelcome = (): void => setState({ phase: 'welcome', preSubMode: null })

  return (
    <div className={styles.container}>
      {state.phase === 'welcome' && (
        <ModulationLabWelcome
          preSubMode={state.preSubMode}
          onStartExplore={startExplore}
          onStartQuiz={startQuiz}
        />
      )}
      {state.phase === 'exploring' && (
        <ModulationExplorePanel
          initialSource={state.source}
          initialTechnique={state.technique}
          initialDestination={state.destination}
        />
      )}
      {state.phase === 'asking-quiz' && (
        <QuizQuestionPanel
          question={state.question}
          onAnswer={handleAnswer}
          questionNumber={state.index + 1}
          totalQuestions={QUESTIONS_PER_SESSION}
        />
      )}
      {state.phase === 'asking-source' && (
        <ModulationSourceKeyPicker
          onPick={handleSourcePick}
          questionNumber={state.index + 1}
          totalQuestions={QUESTIONS_PER_SESSION}
        />
      )}
      {state.phase === 'feedback-quiz' && (
        <ModulationQuizFeedback
          question={state.question}
          verdict={state.verdict}
          onNext={handleNext}
        />
      )}
      {state.phase === 'done-quiz' && (
        <ModulationLabSummary
          stats={computeModulationSessionStats(state.results)}
          onRestart={() => startQuiz({ length: 'standard', difficulty: 'easy' })}
          onExit={exitToWelcome}
        />
      )}
      <RelatedTools tools={RELATED_TOOLS} />
    </div>
  )
}
